/**
 * Image Element Player
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import { useElementUITranslations } from '../i18n/elementUI';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import { resolveMediaUrlForViewer, shouldResolveMediaUrlForViewer } from '@ai-content-hub/content-core';
import { Loader2 } from 'lucide-react';
import type { ImageElementData } from './schema';
import { imageElementImgStyle } from './imageStyle';

export interface ImagePlayerProps extends ElementPlayerProps<ImageElementData> { }

const ImagePlayerInner: React.FC<ImagePlayerProps> = ({ data, className }) => {
    const et = useElementUITranslations();
    const rawSrc = (data?.src ?? '').trim();

    const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(() =>
        rawSrc && !shouldResolveMediaUrlForViewer(rawSrc) ? rawSrc : null,
    );
    const [resolving, setResolving] = React.useState(() =>
        Boolean(rawSrc && shouldResolveMediaUrlForViewer(rawSrc)),
    );

    React.useEffect(() => {
        const s = (data?.src ?? '').trim();
        if (!s || typeof window === 'undefined') {
            setResolvedSrc(null);
            setResolving(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(s)) {
            setResolvedSrc(s);
            setResolving(false);
            return;
        }
        let cancelled = false;
        setResolving(true);
        setResolvedSrc(null);
        void (async () => {
            try {
                const uri = await resolveMediaUrlForViewer(s);
                if (!cancelled) {
                    setResolvedSrc(uri || s);
                }
            } catch {
                if (!cancelled) {
                    setResolvedSrc(s);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [data?.src]);

    // Validate data exists
    if (!data) {
        return (
            <div
                className={cn(
                    'image-element-placeholder p-8 bg-muted rounded-lg text-center',
                    className
                )}
            >
                <span className="text-muted-foreground">{et.noImageData}</span>
            </div>
        );
    }

    const { src, alt = '', caption, size = 'medium', alignment = 'center' } = data;

    // Size classes
    const sizeClasses: Record<string, string> = {
        small: 'max-w-[25%]',
        medium: 'max-w-[50%]',
        large: 'max-w-[75%]',
        full: 'max-w-full w-full',
    };

    // Alignment wrapper classes
    const alignmentClasses: Record<string, string> = {
        left: 'mr-auto',
        center: 'mx-auto',
        right: 'ml-auto',
    };

    if (!src) {
        return (
            <div
                className={cn(
                    'image-element-placeholder p-8 bg-muted rounded-lg text-center',
                    className
                )}
            >
                <span className="text-muted-foreground">{et.noImageSelected}</span>
            </div>
        );
    }

    const displaySrc = resolvedSrc ?? '';
    const showProtectedLoading =
        Boolean(rawSrc && shouldResolveMediaUrlForViewer(rawSrc)) && (resolving || !resolvedSrc);

    return (
        <figure
            className={cn(
                'image-element',
                sizeClasses[size] || sizeClasses.medium,
                alignmentClasses[alignment] || alignmentClasses.center,
                className
            )}
        >
            {showProtectedLoading ? (
                <div
                    className={cn(
                        'image-element-loading flex items-center justify-center gap-2',
                        'min-h-[160px] rounded-lg bg-muted/30 text-sm text-muted-foreground'
                    )}
                >
                    <Loader2 className="h-6 w-6 animate-spin shrink-0" />
                    <span>{et.loadingImage}</span>
                </div>
            ) : (
                // EAA-2063: aspect kept, not upscaled, height capped to the viewport (see
                // imageElementImgStyle) — a full-width picture used to push the question
                // below the fold.
                <img
                    src={displaySrc}
                    alt={alt}
                    className="image-element-img rounded-lg shadow-md"
                    style={imageElementImgStyle(alignment)}
                    loading="lazy"
                />
            )}
            {caption && (
                <figcaption className="text-sm text-muted-foreground text-center mt-2">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
};

export const ImagePlayer = React.memo(ImagePlayerInner);

export default ImagePlayer;
